import { ApiService } from "../api/ApiService";

export interface AgentAnalytics {
  id: string;
  name: string;
  conversationCount: number;
  recentConversations: number;
  lastUsedAt: string | null;
  owner: {
    id: string;
    name: string | null;
    email: string | null;
  } | null;
  brand: string | null;
  branding?: {
    primaryColor?: string | null;
    secondaryColor?: string | null;
    customAvatarUrl?: string | null;
    gradientEnabled?: boolean;
    gradientAngle?: number;
  };
  isTeamAgent: boolean;
}

export interface AnalyticsOverview {
  totalConversations: number;
  totalAgents: number;
  averageConversationsPerAgent: number;
  topPerformingAgent: string | null;
}

export interface TimelineDataPoint {
  date: string;
  conversations: number;
}

export interface AgentTimeline {
  agentId: string;
  agentName: string;
  data: TimelineDataPoint[];
}

type AnalyticsOptions = {
  userId?: string;
  filterByUserId?: string; // For admin filtering by specific user
};

const getAgentAnalytics = async (options?: AnalyticsOptions): Promise<AgentAnalytics[]> => {
  try {
    const data = ApiService.invoke<AgentAnalytics[]>({
      resource: "genies",
      action: "analytics",
      data: {
        adminMode: !!options?.filterByUserId,
        userId: options?.filterByUserId || options?.userId,
      },
    });
    return (await data).sort((a, b) => b.conversationCount - a.conversationCount);
  } catch (error) {
    console.error("Error fetching agent analytics:", error);
    throw error;
  }
};

const getAnalyticsOverview = async (options?: AnalyticsOptions): Promise<AnalyticsOverview> => {
  try {
    const agentAnalytics = await getAgentAnalytics(options);
    const totalAgents = agentAnalytics.length;
    const totalConversations = agentAnalytics.reduce(
      (sum, agent) => sum + agent.conversationCount,
      0,
    );
    const averageConversationsPerAgent =
      totalAgents > 0 ? Math.round(totalConversations / totalAgents) : 0;

    const topPerformingAgent = agentAnalytics.length > 0 ? agentAnalytics[0].name : null;

    return {
      totalConversations,
      totalAgents,
      averageConversationsPerAgent,
      topPerformingAgent,
    };
  } catch (error) {
    console.error("Error fetching analytics overview:", error);
    throw error;
  }
};

const getAgentTimeline = async (
  agentId: string,
  startDate: Date,
  endDate: Date,
): Promise<AgentTimeline> => {
  try {
    return await ApiService.invoke<AgentTimeline>({
      resource: "genies",
      action: "timeline",
      id: agentId,
      data: {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      },
    });
  } catch (error) {
    console.error("Error fetching agent timeline:", error);
    throw error;
  }
};

const getMultipleAgentTimelines = async (
  agentIds: string[],
  startDate: Date,
  endDate: Date,
): Promise<AgentTimeline[]> => {
  try {
    const timelines = await Promise.all(
      agentIds.map((agentId) => getAgentTimeline(agentId, startDate, endDate)),
    );
    return timelines;
  } catch (error) {
    console.error("Error fetching multiple agent timelines:", error);
    throw error;
  }
};

export const AnalyticsService = {
  getAgentAnalytics,
  getAnalyticsOverview,
  getAgentTimeline,
  getMultipleAgentTimelines,
};
